import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Cell, Layout } from '@wix/design-system';
import { CustomField, CustomFieldType } from '@wix/bex-core';
import { FormFieldInput } from '../FormFieldInput';
import { CustomFieldsFormState, CustomFieldsFormSize } from '../../state';
import { CustomFieldsFormContextProvider } from '../CustomFieldsFormContext';

export interface CustomFieldsFormProps {
  state: CustomFieldsFormState;
  dataHook?: string;
}

const toCellSpan = (size: CustomFieldsFormSize, type?: CustomFieldType) => {
  switch (type) {
    case 'longText':
    case 'checkbox':
    case 'shortText':
    case 'dateTime':
      return size === 'default' ? 6 : 8;
    default:
      return size === 'default' ? 3 : 4;
  }
};

const _CustomFieldsForm = ({ dataHook, state }: CustomFieldsFormProps) => {
  useEffect(() => {
    return state.init();
  }, []);

  const renderFieldInput = useCallback(
    (field: CustomField) => {
      if (!field) {
        return null;
      }

      const span = toCellSpan(state.formSize, field.type);

      return (
        <React.Fragment key={field.id}>
          <Cell span={span}>
            <FormFieldInput
              dataHook={`field-${field.id}`}
              schema={field}
              initialValue={state.getInitialValue(field.id)}
              onChange={(newValue) => state.setValue(field.id, newValue)}
              inputRef={(inputRef) => {
                state.setInputRef(field.id, inputRef);
              }}
            />
          </Cell>
          <Cell span={12 - span} />
        </React.Fragment>
      );
    },
    [state.formSize],
  );

  return (
    <CustomFieldsFormContextProvider value={state}>
      <div
        ref={(containerRef) => {
          state.setContainerRef(containerRef);
        }}
      >
        <Layout dataHook={dataHook} gap="SP4">
          {state.schema.map(renderFieldInput)}
        </Layout>
      </div>
    </CustomFieldsFormContextProvider>
  );
};

export const CustomFieldsForm = observer(_CustomFieldsForm);
